/**
 * HUD v1 — DOM overlay (no framework). Stage 1c.
 *
 * Top-left: distance (m) + speed (m/s, 1 decimal). Top-right: pause/play
 * toggle button. Top-center: score + multiplier, and — only while the kick is
 * recharging — a wide kick-cooldown bar that fills toward ready. Phase
 * overlays:
 *  - title:    "HIGHWAY SURFERS — click or press any key to ride".
 *  - crashed:  red flash overlay.
 *  - gameover: run stats (distance, kicks, near-misses, top speed, score) +
 *              RESTART button (calls sim.reset() with a seed from the optional
 *              `nextSeed` provider — a fresh random road per run by default,
 *              the pinned seed when `?seed=<n>` is set).
 *  - paused:   dim overlay + "PAUSED".
 *
 * Pause toggle: button, KeyP, or Escape. Start (title): click canvas, Space, or
 * any sim input (kick edge). Restart (gameover): RESTART button, Enter, Space,
 * or canvas click. Resume (paused): Space, KeyP or Escape. The keydown listener
 * handles Escape / KeyP / Enter / Space; canvas click starts a title run
 * (sim.start()) and restarts from gameover.
 *
 * Minimal inline styles, monospace, high contrast. Zero allocation steady
 * state: `update` mutates textContent / classList / style.width in place.
 */

import type { Sim, WorldState } from "../sim/types";
import { TUNING } from "../sim/tuning";

const K = TUNING.kick;

/** How long the coin counter stays scaled up after an arrival (ms). */
const COIN_PULSE_MS = 180;
/**
 * How far the painted coin count may lag the sim before it SNAPS instead of
 * ticking. One chain is at most `coins.chainMax`; anything beyond that means
 * flights were cancelled (a backgrounded tab, a phase reset) rather than merely
 * in the air.
 */
const COIN_CATCHUP_SLACK = TUNING.coins.chainMax + 2;

/**
 * Format the combo multiplier for display. `score.multiplier` is a continuous
 * float while it decays (scoring.tickDecay), so printing it raw produced
 * strings like "x5.983333333333333". Whole values render as integers ("x6"),
 * everything else to one decimal ("x5.9").
 */
export function formatMultiplier(m: number): string {
  const r = Math.round(m * 10) / 10;
  return Number.isInteger(r) ? `x${r}` : `x${r.toFixed(1)}`;
}

/**
 * DOM HUD controller. Construct with the sim (for reset + state reads) and
 * the canvas (click-to-start / click-to-restart). Call `update` each render
 * frame.
 */
export class Hud {
  private readonly sim: Sim;
  readonly root: HTMLDivElement;
  private readonly distEl: HTMLSpanElement;
  private readonly speedEl: HTMLSpanElement;
  private readonly scoreEl: HTMLSpanElement;
  private readonly multEl: HTMLSpanElement;
  /**
   * COIN COUNTER (top-centre, under the multiplier). The number it shows is
   * NOT `score.coins` directly: it is driven by the magnet-suck sprites
   * LANDING (render/coinFlight.ts calls `bumpCoin`), so the count visibly ticks
   * up one at a time as a chain arrives instead of jumping to the total the
   * instant the sim collects it. `update` re-syncs it to the sim value as a
   * backstop, so a dropped frame or a cancelled flight can never leave the HUD
   * out of step with the score.
   */
  private readonly coinWrapEl: HTMLDivElement;
  private readonly coinDiscEl: HTMLDivElement;
  private readonly coinCountEl: HTMLSpanElement;
  /** The number currently PAINTED — lags `score.coins` by the flight time. */
  private shownCoins = 0;
  /** performance.now() when the current coin pulse ends. */
  private coinPulseUntil = 0;
  /** Kick cooldown bar (top-center, shown only while the kick is recharging). */
  private readonly cdBarEl: HTMLDivElement;
  private readonly cdFillEl: HTMLDivElement;
  /** Last visibility written to `cdBarEl` — avoids redundant style writes. */
  private cdVisible = false;
  private readonly pauseBtn: HTMLButtonElement;
  private readonly overlayEl: HTMLDivElement;
  private readonly titleWrapEl: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly subtitleEl: HTMLDivElement;
  /** Controls legend on the title screen (A/D steer, Space kick). */
  private readonly helpEl: HTMLDivElement;
  private readonly crashFlashEl: HTMLDivElement;
  private readonly gameoverEl: HTMLDivElement;
  private readonly pausedEl: HTMLDivElement;

  private topSpeed = 0;
  /** Best-run record (localStorage hw_best); null when none/unreadable. */
  private best: { distance: number; total: number } | null = null;
  private readonly bestEl: HTMLDivElement;
  private prevMultiplier = 1;
  private multFlashUntil = 0;


  /**
   * @param onSpaceConsumed called when the HUD consumes a Space press for a
   *   phase action (start/restart/resume) — main.ts clears the InputCollector's
   *   kick edge here so the same press never fires a kick on the next sim step.
   * @param onSimReset called immediately after `sim.reset()` (RESTART button,
   *   Enter, Space or canvas click — all DOM handlers that run between rAF
   *   ticks). main.ts re-bases its render-interpolation snapshots here so no
   *   frame interpolates between the old run's distance and 0.
   * @param nextSeed supplies the seed for the NEXT run on every restart. A
   *   fresh random seed means retrying gives you a genuinely different road
   *   instead of replaying the layout you just died to. Returning `undefined`
   *   (or omitting the callback entirely) keeps the current seed, which is what
   *   `?seed=<n>` debugging wants — and is the pre-existing behaviour, so
   *   callers that do not pass it are unaffected.
   */
  constructor(
    sim: Sim,
    canvas: HTMLCanvasElement,
    private readonly onSpaceConsumed?: () => void,
    private readonly onSimReset?: () => void,
    private readonly nextSeed?: () => number | undefined,
  ) {
    this.sim = sim;

    this.root = document.createElement("div");
    this.root.id = "hud";
    Object.assign(this.root.style, {
      // Constrain the overlay to the letterboxed 9:16 viewport (matches the
      // canvas fit box exactly, in pure CSS so it tracks window resizes).
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "min(100vw, 56.25vh)",
      height: "min(100vh, 177.78vw)",
      overflow: "hidden",
      pointerEvents: "none",
      fontFamily: "monospace",
      color: "#fff",
      fontSize: "19px",
      zIndex: "10",
      userSelect: "none",
    } as Partial<CSSStyleDeclaration>);

    // --- Top-left telemetry. ---
    const tl = document.createElement("div");
    Object.assign(tl.style, {
      position: "absolute",
      top: "10px",
      left: "12px",
      textShadow: "0 1px 2px #000",
    } as Partial<CSSStyleDeclaration>);
    this.distEl = document.createElement("span");
    this.distEl.textContent = "0 m";
    this.speedEl = document.createElement("span");
    this.speedEl.textContent = "  0.0 m/s";
    this.speedEl.style.marginLeft = "20px";
    tl.appendChild(this.distEl);
    tl.appendChild(this.speedEl);
    this.root.appendChild(tl);

    // --- Top-center score + multiplier (portrait polish). ---
    const tc = document.createElement("div");
    Object.assign(tc.style, {
      position: "absolute",
      top: "10px",
      left: "50%",
      transform: "translateX(-50%)",
      textShadow: "0 1px 3px #000",
      textAlign: "center",
      whiteSpace: "nowrap",
    } as Partial<CSSStyleDeclaration>);
    this.scoreEl = document.createElement("div");
    Object.assign(this.scoreEl.style, {
      fontSize: "27px",
      fontWeight: "bold",
      letterSpacing: "1px",
    } as Partial<CSSStyleDeclaration>);
    this.scoreEl.textContent = "0";
    this.multEl = document.createElement("div");
    Object.assign(this.multEl.style, {
      fontSize: "18px",
      color: "#ffd24a",
      marginTop: "1px",
      transition: "none",
    } as Partial<CSSStyleDeclaration>);
    this.multEl.textContent = "x1";
    tc.appendChild(this.scoreEl);
    tc.appendChild(this.multEl);
    // --- Coin counter: a small gold disc + count, matching the multiplier's
    //     weight so the score block still reads as one unit.
    this.coinWrapEl = document.createElement("div");
    Object.assign(this.coinWrapEl.style, {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      marginTop: "3px",
      fontSize: "18px",
      color: "#ffc93c",
      transformOrigin: "50% 50%",
    } as Partial<CSSStyleDeclaration>);
    this.coinDiscEl = document.createElement("div");
    Object.assign(this.coinDiscEl.style, {
      width: "14px",
      height: "14px",
      borderRadius: "50%",
      // The same gradient the flying sprites use, so the sprite visibly
      // BECOMES this disc when it lands.
      background:
        "radial-gradient(circle at 34% 30%, #fff3c4 0%, #ffc93c 45%, #d98f14 100%)",
      border: "1px solid rgba(120,72,0,0.55)",
      boxShadow: "0 0 6px rgba(255,201,60,0.6)",
    } as Partial<CSSStyleDeclaration>);
    this.coinCountEl = document.createElement("span");
    this.coinCountEl.textContent = "0";
    this.coinCountEl.style.fontWeight = "bold";
    this.coinWrapEl.appendChild(this.coinDiscEl);
    this.coinWrapEl.appendChild(this.coinCountEl);
    tc.appendChild(this.coinWrapEl);
    this.root.appendChild(tc);

    // --- Kick cooldown bar (top-center, under the score block). ---
    // Only visible WHILE the kick is on cooldown; the fill grows left→right
    // toward "ready", so a full bar means the kick is about to come back.
    this.cdBarEl = document.createElement("div");
    Object.assign(this.cdBarEl.style, {
      position: "absolute",
      // Below the whole top-centre stack: score (27px) + multiplier (18px) +
      // the coin counter row (~18px) plus their margins. Raised from 72px when
      // the coin counter was added — at 72 the bar ran straight through it.
      top: "100px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "55%",
      height: "14px",
      background: "rgba(0,0,0,0.5)",
      border: "1px solid rgba(255,255,255,0.28)",
      borderRadius: "7px",
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.6)",
      display: "none",
      opacity: "0",
      transition: "opacity 120ms linear",
    } as Partial<CSSStyleDeclaration>);
    this.cdFillEl = document.createElement("div");
    Object.assign(this.cdFillEl.style, {
      width: "0%",
      height: "100%",
      background: "linear-gradient(90deg, #e8742c, #ffb347)",
      borderRadius: "7px",
      transition: "none",
    } as Partial<CSSStyleDeclaration>);
    this.cdBarEl.appendChild(this.cdFillEl);
    this.root.appendChild(this.cdBarEl);

    // --- Top-right pause/play button. ---
    this.pauseBtn = document.createElement("button");
    this.pauseBtn.textContent = "❚❚";
    Object.assign(this.pauseBtn.style, {
      position: "absolute",
      top: "8px",
      right: "12px",
      pointerEvents: "auto",
      background: "rgba(0,0,0,0.5)",
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.3)",
      borderRadius: "4px",
      padding: "10px 16px",
      fontFamily: "monospace",
      fontSize: "20px",
      cursor: "pointer",
    } as Partial<CSSStyleDeclaration>);
    this.pauseBtn.addEventListener("click", (e) => {
      e.preventDefault();
      this.togglePause();
    });
    this.root.appendChild(this.pauseBtn);

    // --- Overlays (title / crash flash / gameover / paused). ---
    this.overlayEl = document.createElement("div");
    Object.assign(this.overlayEl.style, {
      position: "absolute",
      inset: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    } as Partial<CSSStyleDeclaration>);

    // --- Title: split into title + subtitle to avoid mid-word breaks. ---
    this.titleWrapEl = document.createElement("div");
    Object.assign(this.titleWrapEl.style, {
      textAlign: "center",
      padding: "0 20px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "10px",
    } as Partial<CSSStyleDeclaration>);
    this.titleEl = document.createElement("div");
    Object.assign(this.titleEl.style, {
      fontSize: "34px",
      fontWeight: "bold",
      textShadow: "0 2px 8px #000",
      letterSpacing: "2px",
      whiteSpace: "nowrap",
    } as Partial<CSSStyleDeclaration>);
    this.titleEl.textContent = "KICKY ROAD";
    this.subtitleEl = document.createElement("div");
    Object.assign(this.subtitleEl.style, {
      fontSize: "18px",
      textShadow: "0 1px 4px #000",
      opacity: "0.85",
    } as Partial<CSSStyleDeclaration>);
    this.subtitleEl.textContent = "click or press any key to ride";
    this.titleWrapEl.appendChild(this.titleEl);
    this.titleWrapEl.appendChild(this.subtitleEl);
    // Controls help chrome (owner request 2026-08-03, pre-publish): a compact
    // key legend on the title screen only — hidden with the rest of the title
    // overlay once the run starts.
    this.helpEl = document.createElement("div");
    Object.assign(this.helpEl.style, {
      display: "flex",
      gap: "18px",
      justifyContent: "center",
      alignItems: "center",
      fontSize: "15px",
      textShadow: "0 1px 4px #000",
      opacity: "0.9",
      marginTop: "6px",
    } as Partial<CSSStyleDeclaration>);
    const helpItem = (keys: string[], label: string): HTMLDivElement => {
      const item = document.createElement("div");
      Object.assign(item.style, {
        display: "flex",
        gap: "6px",
        alignItems: "center",
      } as Partial<CSSStyleDeclaration>);
      for (const k of keys) {
        const kbd = document.createElement("span");
        kbd.textContent = k;
        Object.assign(kbd.style, {
          border: "1px solid rgba(255,255,255,0.7)",
          borderRadius: "5px",
          padding: "2px 8px",
          fontWeight: "bold",
          fontSize: "14px",
          background: "rgba(0,0,0,0.35)",
        } as Partial<CSSStyleDeclaration>);
        item.appendChild(kbd);
      }
      const lab = document.createElement("span");
      lab.textContent = label;
      item.appendChild(lab);
      return item;
    };
    this.helpEl.appendChild(helpItem(["A", "D"], "steer"));
    this.helpEl.appendChild(helpItem(["SPACE"], "kick"));
    this.titleWrapEl.appendChild(this.helpEl);
    this.bestEl = document.createElement("div");
    Object.assign(this.bestEl.style, {
      fontSize: "18px",
      color: "#ffd24a",
      textShadow: "0 1px 4px #000",
      display: "none",
    } as Partial<CSSStyleDeclaration>);
    this.titleWrapEl.appendChild(this.bestEl);
    this.overlayEl.appendChild(this.titleWrapEl);
    this.crashFlashEl = document.createElement("div");
    Object.assign(this.crashFlashEl.style, {
      position: "absolute",
      inset: "0",
      background: "rgba(220,30,30,0.45)",
      display: "none",
    } as Partial<CSSStyleDeclaration>);
    this.overlayEl.appendChild(this.crashFlashEl);

    this.pausedEl = document.createElement("div");
    Object.assign(this.pausedEl.style, {
      fontSize: "36px",
      fontWeight: "bold",
      textShadow: "0 2px 6px #000",
      display: "none",
    } as Partial<CSSStyleDeclaration>);
    this.pausedEl.textContent = "PAUSED";
    this.overlayEl.appendChild(this.pausedEl);

    this.gameoverEl = document.createElement("div");
    Object.assign(this.gameoverEl.style, {
      display: "none",
      flexDirection: "column",
      alignItems: "center",
      gap: "8px",
      pointerEvents: "auto",
      background: "rgba(0,0,0,0.7)",
      border: "1px solid rgba(255,255,255,0.25)",
      borderRadius: "8px",
      padding: "28px 44px",
      textShadow: "0 1px 3px #000",
    } as Partial<CSSStyleDeclaration>);
    this.gameoverEl.innerHTML = "";
    this.overlayEl.appendChild(this.gameoverEl);

    this.root.appendChild(this.overlayEl);
    document.body.appendChild(this.root);

    // Load best-run record (privacy-mode safe).
    try {
      const raw = localStorage.getItem("hw_best");
      if (raw) {
        const parsed = JSON.parse(raw) as { distance?: unknown; total?: unknown };
        if (typeof parsed.distance === "number" && typeof parsed.total === "number") {
          this.best = { distance: parsed.distance, total: parsed.total };
        }
      }
    } catch {
      this.best = null;
    }

    // --- Input wiring -------------------------------------------------------
    // KeyP or Escape toggles pause. Space/Enter start (title), restart (gameover), or
    // resume (paused) — matching the click behaviour. Canvas click starts a
    // title run or restarts from gameover.
    this.onKeyDown = (e: KeyboardEvent) => {
      // Auto-repeat is not a new press: a HELD Space/Enter/P must behave
      // exactly like a TAP (one phase action), never a stream of them.
      if (e.repeat) return;
      const phase = this.sim.state.phase;
      if (e.code === "KeyP" || e.code === "Escape") {
        // Escape is the pause key players reach for first (owner playtest). It
        // goes through the SAME `togglePause` path as KeyP and the button, so
        // it is a no-op on title/crashed/gameover — Escape must never be able to
        // pause a run that has not started or a crash that is playing out.
        e.preventDefault();
        this.togglePause();
      } else if (e.code === "Enter" && phase === "gameover") {
        e.preventDefault();
        this.restart();
      } else if (e.code === "Space") {
        if (phase === "title") {
          e.preventDefault();
          this.sim.start();
          this.onSpaceConsumed?.();
        } else if (phase === "gameover") {
          e.preventDefault();
          this.restart();
          this.onSpaceConsumed?.();
        } else if (phase === "paused") {
          e.preventDefault();
          this.sim.resume();
          this.onSpaceConsumed?.();
        }
      }
    };
    window.addEventListener("keydown", this.onKeyDown);

    this.onCanvasClick = (e: MouseEvent) => {
      e.preventDefault();
      const phase = this.sim.state.phase;
      if (phase === "title") {
        this.sim.start();
      } else if (phase === "gameover") {
        this.restart();
      }
    };
    canvas.style.cursor = "pointer";
    canvas.addEventListener("click", this.onCanvasClick);
  }

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onCanvasClick: (e: MouseEvent) => void;

  /** Toggle pause/resume via the sim. */
  private togglePause(): void {
    const phase = this.sim.state.phase;
    if (phase === "playing") this.sim.pause();
    else if (phase === "paused") this.sim.resume();
  }

  private restart(): void {
    // `reset(undefined)` keeps the current seed (World.reset's own default), so
    // an absent provider is exactly the old behaviour.
    this.sim.reset(this.nextSeed?.());
    this.onSimReset?.();
    this.topSpeed = 0;
    this.prevMultiplier = 1;
    this.multFlashUntil = 0;
    this.shownCoins = 0;
    this.coinPulseUntil = 0;
  }

  /**
   * ONE magnet sprite has landed on the counter (render/coinFlight.ts).
   *
   * Increments the painted count and pulses it. Deliberately NOT clamped to
   * `score.coins` here: the arrival always follows its own pickup, and `update`
   * reconciles both directions on the next frame anyway.
   */
  bumpCoin(): void {
    this.shownCoins += 1;
    this.coinPulseUntil = performance.now() + COIN_PULSE_MS;
  }

  /**
   * Centre of the coin counter in viewport pixels — where the magnet sprites
   * fly to. Measured from the live layout (the HUD is a flex column whose
   * height depends on the font), and cached until the viewport changes, so the
   * per-pickup cost is not a forced reflow.
   */
  coinTarget(): { x: number; y: number } {
    const w = this.root.clientWidth;
    const h = this.root.clientHeight;
    if (this.coinTargetW !== w || this.coinTargetH !== h) {
      this.coinTargetW = w;
      this.coinTargetH = h;
      const r = this.coinWrapEl.getBoundingClientRect();
      const root = this.root.getBoundingClientRect();
      this.coinTargetX = r.left - root.left + r.width / 2;
      this.coinTargetY = r.top - root.top + r.height / 2;
    }
    return { x: this.coinTargetX, y: this.coinTargetY };
  }

  private coinTargetX = 0;
  private coinTargetY = 0;
  private coinTargetW = -1;
  private coinTargetH = -1;

  /** Render-frame update: refresh telemetry + overlays from sim state. */
  update(state: WorldState): void {
    const p = state.player;
    const s = state.score;
    // Track top speed for the gameover panel.
    if (p.forwardSpeed > this.topSpeed) this.topSpeed = p.forwardSpeed;

    this.distEl.textContent = `${Math.floor(p.distance)} m`;
    this.speedEl.textContent = `  ${p.forwardSpeed.toFixed(1)} m/s`;

    // --- Top-center score + multiplier (portrait polish). ---
    this.scoreEl.textContent = `${s.total}`;
    // Multiplier flash on INCREMENT only. `score.multiplier` decays
    // continuously (scoring.tickDecay), so an inequality test retriggered the
    // flash on every frame of the decay ramp and pinned it at 22 px.
    if (s.multiplier > this.prevMultiplier) {
      this.multFlashUntil = performance.now() + 300;
    }
    this.prevMultiplier = s.multiplier;
    const flashing = performance.now() < this.multFlashUntil;
    this.multEl.textContent = formatMultiplier(s.multiplier);
    this.multEl.style.fontSize = flashing ? "22px" : "18px";
    this.multEl.style.color = flashing ? "#fff" : "#ffd24a";
    this.multEl.style.textShadow = flashing
      ? "0 0 8px #ffd24a, 0 1px 4px #000"
      : "0 1px 3px #000";

    // --- Coin counter. The painted number is driven by the magnet sprites
    //     landing (`bumpCoin`), so it ticks up one at a time as a chain
    //     arrives. This reconciles it with the sim in BOTH directions:
    //     sprites that were cancelled (a crash mid-flight, a restart) would
    //     otherwise leave it short forever, and it must never run ahead.
    if (this.shownCoins > s.coins) this.shownCoins = s.coins;
    else if (s.coins - this.shownCoins > COIN_CATCHUP_SLACK) {
      // Far behind: a burst was dropped (backgrounded tab, phase reset). Snap
      // rather than dribble — the number is information first, juice second.
      this.shownCoins = s.coins;
      this.coinPulseUntil = performance.now() + COIN_PULSE_MS;
    }
    this.coinCountEl.textContent = `${this.shownCoins}`;
    const coinPulse = performance.now() < this.coinPulseUntil;
    this.coinWrapEl.style.transform = coinPulse ? "scale(1.28)" : "scale(1)";
    this.coinCountEl.style.color = coinPulse ? "#fff6d0" : "#ffc93c";

    // Kick cooldown bar: visible ONLY while recharging; fills toward ready.
    const onCooldown = p.kickCooldownRemaining > 0;
    if (onCooldown) {
      const frac = Math.max(0, Math.min(1, 1 - p.kickCooldownRemaining / K.cooldown));
      this.cdFillEl.style.width = `${frac * 100}%`;
    }
    if (onCooldown !== this.cdVisible) {
      this.cdVisible = onCooldown;
      this.cdBarEl.style.display = onCooldown ? "block" : "none";
      this.cdBarEl.style.opacity = onCooldown ? "1" : "0";
    }

    // Pause button label.
    this.pauseBtn.textContent = state.phase === "paused" ? "▶" : "❚❚";

    // Overlay visibility per phase.
    this.titleWrapEl.style.display = state.phase === "title" ? "flex" : "none";
    if (state.phase === "title") {
      if (this.best) {
        this.bestEl.textContent = `BEST: ${Math.floor(this.best.distance)} m`;
        this.bestEl.style.display = "block";
      } else {
        this.bestEl.style.display = "none";
      }
    }
    this.crashFlashEl.style.display = state.phase === "crashed" ? "block" : "none";
    this.pausedEl.style.display = state.phase === "paused" ? "block" : "none";

    if (state.phase === "gameover") {
      if (this.gameoverEl.style.display !== "flex") {
        this.gameoverEl.style.display = "flex";
        this.gameoverEl.innerHTML = "";
        // Best score: compare, flag NEW BEST, persist BEFORE rendering rows.
        const distM = Math.floor(p.distance);
        const isNewBest = this.best === null || s.total > this.best.total;
        if (isNewBest) {
          this.best = { distance: distM, total: s.total };
          try {
            localStorage.setItem("hw_best", JSON.stringify(this.best));
          } catch {
            // storage unavailable — session-only best
          }
        }
        if (isNewBest) {
          const nb = document.createElement("div");
          nb.textContent = "NEW BEST!";
          Object.assign(nb.style, {
            color: "#ffd24a",
            fontWeight: "bold",
            fontSize: "28px",
            letterSpacing: "2px",
            textShadow: "0 0 10px #ffd24a, 0 1px 4px #000",
          });
          this.gameoverEl.appendChild(nb);
        }
        const lines = [
          ["DISTANCE", `${distM} m`],
          ["TOP SPEED", `${this.topSpeed.toFixed(1)} m/s`],
          ["KICKS", `${s.kicks}`],
          ["NEAR-MISSES", `${s.nearMisses}`],
          ["COINS", `${s.coins}`],
          ["SCORE", `${s.total}`],
          ["BEST", `${Math.floor(this.best?.distance ?? distM)} m`],
        ];
        for (const [label, val] of lines) {
          const row = document.createElement("div");
          Object.assign(row.style, {
            display: "flex",
            justifyContent: "space-between",
            gap: "24px",
            width: "360px",
            fontSize: "22px",
          } as Partial<CSSStyleDeclaration>);
          const l = document.createElement("span");
          l.textContent = label;
          l.style.opacity = "0.7";
          const v = document.createElement("span");
          v.textContent = val;
          v.style.fontWeight = "bold";
          row.appendChild(l);
          row.appendChild(v);
          this.gameoverEl.appendChild(row);
        }
        // Bigger RESTART button for portrait tap targets.
        const btn = document.createElement("button");
        btn.textContent = "RESTART";
        Object.assign(btn.style, {
          marginTop: "20px",
          padding: "18px 52px",
          background: "#e8742c",
          color: "#fff",
          border: "none",
          borderRadius: "8px",
          fontFamily: "monospace",
          fontSize: "26px",
          fontWeight: "bold",
          cursor: "pointer",
          pointerEvents: "auto",
          letterSpacing: "1px",
        } as Partial<CSSStyleDeclaration>);
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.restart();
        });
        this.gameoverEl.appendChild(btn);
      }
    } else {
      this.gameoverEl.style.display = "none";
    }
  }

  /** Tear down DOM + listeners. */
  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    this.root.remove();
  }
}
